import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, Mail } from "lucide-react";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/formations", label: "Formations" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/contact", label: "Contact" },
];

const PHONE = "0749124282";
const PHONE_DISPLAY = "07 49 12 42 82";
const WHATSAPP = "33749124282";
const EMAIL = "skoolformation93@hotmail.com";

// Luxury light palette
const C = {
  bg: "#FAFAF8",
  white: "#FFFFFF",
  navy: "#1B2A4A",
  navyLight: "rgba(27,42,74,0.07)",
  gold: "#C9A452",
  goldLight: "rgba(201,164,82,0.12)",
  text: "#111827",
  muted: "#6B7280",
  light: "#9CA3AF",
  border: "#E8E3D8",
  borderLight: "#F0EDE6",
};

export default function Layout({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: C.bg, color: C.text }}>

      {/* ── Top bar ── */}
      <div style={{ background: C.navy, color: "#fff", padding: "8px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: C.gold }}>●</span> Formation professionnelle sérieuse — Bobigny, Île-de-France
          </div>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <a href={`tel:${PHONE}`} style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}>
              <Phone size={11} /> {PHONE_DISPLAY}
            </a>
            <a href={`mailto:${EMAIL}`} style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}>
              <Mail size={11} /> {EMAIL}
            </a>
          </div>
        </div>
      </div>

      {/* ── Navbar ── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        background: scrolled ? "rgba(250,250,248,0.97)" : C.bg,
        borderBottom: `1px solid ${scrolled ? C.border : "transparent"}`,
        backdropFilter: "blur(16px)",
        transition: "all 0.3s",
        boxShadow: scrolled ? "0 2px 20px rgba(27,42,74,0.06)" : "none",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", height: 72 }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10,
              background: C.navy,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: 18, color: C.gold,
              boxShadow: "0 4px 14px rgba(27,42,74,0.2)",
            }}>S</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: C.navy, letterSpacing: "0.04em", lineHeight: 1.2 }}>
                SKOOL <span style={{ color: C.gold }}>FORMATION</span>
              </div>
              <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                Taxi · VTC · Bobigny
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav style={{ marginLeft: "auto", display: "flex", gap: 2, alignItems: "center" }} className="desk-nav">
            {NAV.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link key={n.to} to={n.to} style={{
                  padding: "9px 18px", borderRadius: 8, fontSize: 14, fontWeight: active ? 600 : 500,
                  color: active ? C.navy : C.muted,
                  background: active ? C.navyLight : "transparent",
                  textDecoration: "none", transition: "all 0.2s",
                  borderBottom: active ? `2px solid ${C.gold}` : "2px solid transparent",
                }}>{n.label}</Link>
              );
            })}
            <Link to="/inscription" style={{
              marginLeft: 12, padding: "10px 22px", borderRadius: 8,
              background: C.navy, color: "#fff", fontWeight: 700, fontSize: 14,
              textDecoration: "none",
              boxShadow: "0 4px 14px rgba(27,42,74,0.25)",
              transition: "all 0.2s",
            }}>S'inscrire</Link>
          </nav>

          {/* Mobile burger */}
          <button onClick={() => setOpen(!open)} className="mob-burger" style={{ marginLeft: "auto", background: "none", border: "none", color: C.navy, cursor: "pointer", padding: 8 }}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div style={{ background: C.white, borderTop: `1px solid ${C.border}`, padding: "12px 24px 20px" }}>
            {[...NAV, { to: "/inscription", label: "S'inscrire" }].map((n) => (
              <Link key={n.to} to={n.to} style={{
                display: "block", padding: "12px 0", color: location.pathname === n.to ? C.navy : C.muted,
                textDecoration: "none", fontWeight: 500, fontSize: 15,
                borderBottom: `1px solid ${C.borderLight}`,
              }}>{n.label}</Link>
            ))}
            <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
              <a href={`tel:${PHONE}`} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "11px 0", borderRadius: 8, background: C.navy, color: "#fff", textDecoration: "none", fontWeight: 600, fontSize: 13 }}>
                <Phone size={14} />{PHONE_DISPLAY}
              </a>
              <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "11px 0", borderRadius: 8, background: "#16a34a", color: "#fff", textDecoration: "none", fontWeight: 600, fontSize: 13 }}>
                <MessageCircle size={14} />WhatsApp
              </a>
            </div>
          </div>
        )}
      </header>

      {/* ── Main ── */}
      <main style={{ flex: 1 }}>{children}</main>

      {/* ── Footer ── */}
      <footer style={{ background: C.navy, color: "rgba(255,255,255,0.85)", padding: "56px 24px 28px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 40, marginBottom: 40 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: C.gold, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: C.navy }}>S</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "#fff", letterSpacing: "0.04em" }}>SKOOL <span style={{ color: C.gold }}>FORMATION</span></div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Taxi · VTC · Bobigny</div>
                </div>
              </div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>
                Centre de formation professionnelle fondé et dirigé par une experte du secteur. Votre réussite, notre mission.
              </p>
              <div style={{ marginTop: 12, fontSize: 11, color: "rgba(255,255,255,0.35)" }}>SIREN : 999 479 785 — RCS Bobigny</div>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 16 }}>Navigation</div>
              {[...NAV, { to: "/inscription", label: "S'inscrire" }].map((n) => (
                <Link key={n.to} to={n.to} style={{ display: "block", color: "rgba(255,255,255,0.6)", textDecoration: "none", fontSize: 13, marginBottom: 9 }}>
                  {n.label}
                </Link>
              ))}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 16 }}>Contact</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 2.2 }}>
                <div>📍 8 Rue Layla Zana, 93000 Bobigny</div>
                <div><a href={`tel:${PHONE}`} style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>📞 {PHONE_DISPLAY}</a></div>
                <div><a href={`mailto:${EMAIL}`} style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>✉️ {EMAIL}</a></div>
                <div><a href="https://www.instagram.com/skool_formation" target="_blank" rel="noreferrer" style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>📸 @skool_formation</a></div>
              </div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 20, fontSize: 12, color: "rgba(255,255,255,0.35)", display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 8 }}>
            <span>© 2026 SKOOL FORMATION SASU — Présidente : Sonia</span>
            <span>Formation professionnelle Taxi & VTC — Île-de-France</span>
          </div>
        </div>
      </footer>

      {/* ── Floating Buttons ── */}
      <div style={{ position: "fixed", bottom: 28, right: 24, zIndex: 999, display: "flex", flexDirection: "column", gap: 12 }}>
        <a href={`https://wa.me/${WHATSAPP}?text=Bonjour%20SKOOL%20FORMATION%2C%20je%20souhaite%20des%20informations%20sur%20vos%20formations.`}
          target="_blank" rel="noreferrer"
          style={{ width: 54, height: 54, borderRadius: "50%", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(22,163,74,0.45)", textDecoration: "none" }}>
          <MessageCircle size={24} color="#fff" />
        </a>
        <a href={`tel:${PHONE}`}
          style={{ width: 54, height: 54, borderRadius: "50%", background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(27,42,74,0.35)", textDecoration: "none" }}>
          <Phone size={22} color="#fff" />
        </a>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Playfair+Display:wght@600;700&display=swap');
        * { box-sizing: border-box; font-family: 'Inter', sans-serif; }
        body { margin: 0; background: #FAFAF8; }
        @media (max-width: 768px) { .desk-nav { display: none !important; } .mob-burger { display: flex !important; } }
        @media (min-width: 769px) { .desk-nav { display: flex !important; } .mob-burger { display: none !important; } }
        a:hover { opacity: 0.82; }
      `}</style>
    </div>
  );
}
